import React, { useEffect, useRef, useState } from "react";
import { Share2, MessageCircle, Facebook, Link2, Check, Instagram, Send } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

/**
 * Universal "Share" button — opens a small popover offering:
 *   • WhatsApp (wa.me/?text=...)
 *   • Facebook (facebook.com/sharer)
 *   • Copy link (with confirmation toast)
 *   • Native share sheet (navigator.share, when available on mobile)
 *
 * Drop this onto any service card / illness / audio recitation so every
 * visitor can forward the offer to family and friends in one tap.
 */
const ShareMenu = ({ title, url, className = "", compact = false }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const text = title ? `📖 ${title} — Islamic Cure & Care` : "Islamic Cure & Care";

  const waHref = `https://wa.me/?text=${encodeURIComponent(`${text}\n${shareUrl}`)}`;
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(text)}`;
  const tgHref = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`;
  const [igCopied, setIgCopied] = useState(false);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  const handleClick = async () => {
    // Prefer native share sheet on mobile — offers all installed apps
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        return;
      } catch (e) {
        // User cancelled or share not supported — fall through to menu
      }
    }
    setOpen((v) => !v);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // clipboard not available — ignore
    }
  };

  /** Instagram has no direct web share URL. Best-effort UX: copy the caption
   *  + link to clipboard, then open Instagram so the user can paste into their
   *  Story or DM. On mobile the native share sheet (above) already includes
   *  Instagram, so this menu entry is the desktop fallback. */
  const shareToInstagram = async () => {
    try {
      await navigator.clipboard.writeText(`${text}\n${shareUrl}`);
      setIgCopied(true);
      setTimeout(() => setIgCopied(false), 2000);
    } catch (e) {
      // ignore
    }
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };

  return (
    <div className={`relative inline-block ${className}`} ref={ref}>
      <button
        type="button"
        onClick={handleClick}
        data-testid="share-menu-btn"
        title={t("share_label")}
        className={`inline-flex items-center gap-2 border rounded-full transition ${
          compact
            ? "px-3 py-1.5 text-xs bg-cream border-forest/20 text-forest hover:bg-white"
            : "px-4 py-2 text-sm bg-cream border-forest/25 text-forest hover:bg-white"
        }`}
      >
        <Share2 className="w-4 h-4" />
        {!compact && <span>{t("share_label")}</span>}
      </button>

      {open && (
        <div
          data-testid="share-menu-panel"
          className="absolute right-0 mt-2 z-30 bg-white rounded-2xl shadow-2xl border border-gold/25 p-2 min-w-[190px]"
          style={{ direction: "ltr" }}
        >
          <a
            data-testid="share-whatsapp"
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-cream text-sm text-ink w-full"
          >
            <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp
          </a>
          <a
            data-testid="share-facebook"
            href={fbHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-cream text-sm text-ink w-full"
          >
            <Facebook className="w-4 h-4 text-[#1877F2]" /> Facebook
          </a>
          <button
            type="button"
            onClick={shareToInstagram}
            data-testid="share-instagram"
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-cream text-sm text-ink w-full text-left"
          >
            {igCopied ? (
              <>
                <Check className="w-4 h-4 text-forest" /> {t("share_ig_copied")}
              </>
            ) : (
              <>
                <Instagram className="w-4 h-4 text-[#E1306C]" /> Instagram
              </>
            )}
          </button>
          <a
            data-testid="share-telegram"
            href={tgHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-cream text-sm text-ink w-full"
          >
            <Send className="w-4 h-4 text-[#26A5E4]" /> Telegram
          </a>
          <button
            type="button"
            onClick={copyLink}
            data-testid="share-copy-link"
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-cream text-sm text-ink w-full text-left"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-forest" /> {t("share_copied")}
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4 text-forest" /> {t("share_copy_link")}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareMenu;
